import * as df from 'date-fns';
import { remove } from 'lodash-es';
import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import BooksTextButton from '../../../../../molecules/merchant/resources/book/common/buttons/BooksTextButton';
import BooksSelectedDateTime from '../../../../../molecules/merchant/resources/book/common/inputs/BooksSelectedDateTime';
import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import {
  EventTimeSlot,
  SettingEvent,
} from '../../../../../interfaces/merchants/resources/books';
import { EventTimeSlot as InputsEventTimeSlot } from '../../../../../interfaces/merchants/resources/books/inputs';
import {
  tick,
  back,
} from '../../../../../redux/actions/merchants/resources/books/workflow';
import { default as Header } from '../../../../../molecules/merchant/resources/book/InputBookingEventDateTimeCommonHeader';
import InputSchoolCalendarDateTimePicker from '../../../../../molecules/merchant/resources/book/InputSchoolCalendarDateTimePicker';

import css from './InputBookingEventDateTimeCalendar.module.scss';
import { RSButton } from 'src/reservation-page/RSButton';

interface Props {
  loggedIn: boolean;
  initialEventTimeSlot?: InputsEventTimeSlot;
  settings: SettingEvent;
  postable: boolean;
  onSetDateTimePicker: (query: { start: Date; end: Date }) => Promise<void>;
  onAddToWaitingList: (timeSlot: EventTimeSlot) => Promise<void>;
  onSubmit: (timeSlots: InputsEventTimeSlot) => void;
  withExpressFlow: boolean;
}

const InputBookingEventDateTimeCalendar: React.FC<Props> = (props) => {
  const {
    initialEventTimeSlot,
    loggedIn,
    postable,
    onAddToWaitingList,
    withExpressFlow,
    settings,
  } = props;
  const intl = useIntl();

  const [selecting, setSelecting] = useState<boolean>(false);
  const [baseDate, setBaseDate] = useState<Date>(
    initialEventTimeSlot
      ? df.parse(initialEventTimeSlot.timeSlots[0].date)
      : settings.selectedTimeSlot
      ? df.parse(settings.selectedTimeSlot.date)
      : new Date(),
  );
  const [attendees, setAttendees] = useState<number | undefined>(
    initialEventTimeSlot
      ? initialEventTimeSlot.attendees
      : settings.selectedTimeSlot
      ? 1
      : undefined,
  );
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<EventTimeSlot[]>(
    initialEventTimeSlot
      ? initialEventTimeSlot.timeSlots
      : settings.selectedTimeSlot
      ? [settings.selectedTimeSlot]
      : [],
  );

  const handleChangeAttendees = useCallback((attendees: number) => {
    setAttendees(attendees);
    setSelectedTimeSlots([]);
  }, []);

  const handleStartSelecting = useCallback(() => {
    tick({
      onForwardCallback: () => {
        setSelecting(true);
      },
      onBackCallback: () => {
        setSelecting(false);
      },
    });
    setSelecting(true);
  }, []);

  const handleChangeTimeSlots = useCallback((timeSlots: EventTimeSlot[]) => {
    back();
    setSelecting(false);
    setSelectedTimeSlots(timeSlots);
  }, []);

  const handleRemoveTimeSlot = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const {
        currentTarget: { value },
      } = e;

      const tmpSelectedTimeSlots = [...selectedTimeSlots];
      remove(tmpSelectedTimeSlots, (t) => t.id === parseInt(value, 10));

      setSelectedTimeSlots(tmpSelectedTimeSlots);
    },
    [selectedTimeSlots],
  );

  const handleChangeBaseDate = useCallback(
    (baseDate: Date) => {
      const { onSetDateTimePicker } = props;

      setBaseDate(baseDate);
      onSetDateTimePicker({
        start: df.startOfMonth(baseDate),
        end: df.endOfMonth(baseDate),
      });
    },
    [props],
  );

  const handleSubmit = useCallback(() => {
    props.onSubmit({
      timeSlots: selectedTimeSlots,
      attendees: attendees || 1,
    });
  }, [props, selectedTimeSlots, attendees]);

  return !selecting ? (
    <div className={css.InputBookingEventDateTimeCalendar}>
      <Header
        settings={settings}
        attendees={attendees}
        onChangeAttendees={handleChangeAttendees}
        withExpressFlow={withExpressFlow}
      />
      <LabeledContainer
        label={
          settings.multipleSelectSlots
            ? intl.formatMessage({
                id: 'books.formsLabel.multipleBookingDates',
              })
            : intl.formatMessage({
                id: 'books.booksMenu.bookDate',
              })
        }
        paddingTop={true}
      >
        {selectedTimeSlots.map((timeSlot) => (
          <BooksSelectedDateTime
            key={timeSlot.id}
            date={timeSlot.date}
            startTime={timeSlot.startTime}
            endTime={timeSlot.endTime}
            onTimeSlotClick={
              settings.multipleSelectSlots ? null : handleStartSelecting
            }
            onDeleteClick={
              settings.multipleSelectSlots ? handleRemoveTimeSlot : null
            }
            value={timeSlot.id}
            withBorder={settings.multipleSelectSlots}
          />
        ))}
        {!settings.multipleAttendees.allowed ||
        (settings.multipleAttendees.allowed && !!attendees) ? (
          selectedTimeSlots.length === 0 || settings.multipleSelectSlots ? (
            <BooksTextButton onClick={handleStartSelecting}>
              {intl.formatMessage({
                id: 'books.forms.addDateAndTime',
              })}
            </BooksTextButton>
          ) : null
        ) : (
          <span className={css.InputBookingEventDateTimeCalendar__PlaceHolder}>
            {intl.formatMessage({
              id: 'books.forms.pleaseSelectAttendeesFirst',
            })}
          </span>
        )}
      </LabeledContainer>
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={selectedTimeSlots.length === 0 || !postable}
          onClick={handleSubmit}
        >
          {intl.formatMessage({ id: 'books.submitLabel.next' })}
        </RSButton>
      </div>
    </div>
  ) : (
    <InputSchoolCalendarDateTimePicker
      postable={postable}
      selection={settings.multipleSelectSlots ? 'multiple' : 'single'}
      baseDate={baseDate}
      timeSlots={settings.timeSlots}
      showVacancy={settings.showVacancy}
      onChange={handleChangeTimeSlots}
      onChangeBaseDate={handleChangeBaseDate}
      selectedTimeSlots={selectedTimeSlots}
      attendees={attendees}
      waitingList={
        settings.waitingListProvided
          ? {
              list: settings.waitingList,
              onAddToWaitingList,
              loggedIn,
              errors: settings!.addWaitingListErrors,
            }
          : undefined
      }
    />
  );
};

export default InputBookingEventDateTimeCalendar;
